// HomePageAdmin.js
import React, { useEffect, useState, useCallback, useMemo, memo } from "react";
import { useNavigate } from "react-router-dom";
import axiosConfig from "../../api/axiosConfig";
import { FiUser, FiCalendar, FiActivity, FiTrendingUp, FiBook, FiUsers, FiDollarSign, FiClock, FiBriefcase, FiUserCheck, FiFileText, FiCreditCard, FiHome, FiEye } from 'react-icons/fi';
import TeacherPaymentDetailsModal from './components/TeacherPaymentDetailsModal';
import PaymentDetailsModal from './components/PaymentDetailsModal';
import CompanyRevenueDetailsModal from './components/CompanyRevenueDetailsModal';

// Constants
const SUPER_ADMIN_ID = 'superadmin-001';
const DATE_FORMAT_OPTIONS = { day: 'numeric', month: 'long', year: 'numeric' };
const INITIAL_STATS = {
  studentCount: 0,
  teacherCount: 0,
  lessonCount: 0,
  companyCount: 0,
  activeStudents: 0,
  activeTeachers: 0,
  completedLessons: 0,
  studentsStartedLessons: 0,
  monthlyRevenue: 0
};
const INITIAL_DAILY_STATS = {
  dailyLessons: 0,
  dailyLessonRevenue: 0,
  dailyPayments: 0,
  dailyCourseRegistrations: 0,
  dailyCompanyRevenue: 0,
  dailyConsultantPayments: 0
};
const INITIAL_PAYMENT_STATS = { today: 0, week: 0, year: 0 };
const INITIAL_REVENUE = {
  teacherPayments: 0,
  companyRevenue: 0,
  consultantPayments: 0,
  totalRevenue: 0
};
const INITIAL_LESSON_STATS = { today: 0, week: 0, month: 0, year: 0 };

// Memoized Components
const StatCard = memo(({ icon: Icon, label, value, subValue, color, trend, details }) => (
  <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 p-6">
    <div className="flex items-center justify-between mb-4">
      <div className={`p-3 rounded-xl bg-gradient-to-r ${color}`}>
        <Icon className="text-white text-xl" />
      </div>
      {trend && (
        <div className="flex items-center text-sm text-green-600">
          <FiTrendingUp className="mr-1" />
          <span>{trend}%</span>
        </div>
      )}
    </div>
    <h3 className="text-2xl font-bold text-gray-800">{value}</h3>
    <p className="text-sm text-gray-500 mt-1">{label}</p>
    {subValue && (
      <p className="text-xs text-gray-400 mt-2">{subValue}</p>
    )}
    {details && (
      <div className="mt-3 pt-3 border-t border-gray-100 space-y-1">
        {details.map((detail, index) => (
          <div key={index} className="flex justify-between items-center">
            <span className="text-xs text-gray-500">{detail.label}</span>
            <span className={`text-xs font-semibold ${detail.color || 'text-gray-700'}`}>
              {detail.value}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
));

const ActivityItem = memo(({ user, action, time, type }) => {
  const getActivityIcon = () => {
    switch(type) {
      case 'enrollment': return <FiUser className="text-blue-500" />;
      case 'teacher': return <FiUserCheck className="text-green-500" />;
      case 'lesson': return <FiBook className="text-purple-500" />;
      default: return <FiActivity className="text-gray-500" />;
    }
  };

  return (
    <div className="flex items-center space-x-3 py-3 border-b border-gray-100 last:border-0">
      <div className="p-2 bg-gray-50 rounded-lg">
        {getActivityIcon()}
      </div>
      <div className="flex-1">
        <p className="text-sm text-gray-800">
          <span className="font-medium">{user}</span> {action}
        </p>
        <p className="text-xs text-gray-500 mt-1">{time}</p>
      </div>
    </div>
  );
});

// Utility Functions
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

const getTimeAgo = (dateString) => {
  if (!dateString) return 'bilinmiyor';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now - date;
  const diffInMinutes = Math.floor(diffInMs / 60000);
  const diffInHours = Math.floor(diffInMs / 3600000);
  const diffInDays = Math.floor(diffInMs / 86400000);

  if (diffInMinutes < 1) return 'az önce';
  if (diffInMinutes < 60) return `${diffInMinutes} dakika önce`;
  if (diffInHours < 24) return `${diffInHours} saat önce`;
  return `${diffInDays} gün önce`;
};

const calculateMonthlyRevenue = (students) => {
  // Bu fonksiyon öğrenci verilerine göre aylık geliri hesaplayabilir
  return 0;
};

const getTodayString = () => {
  const today = new Date();
  return today.getFullYear() + '-' + 
         String(today.getMonth() + 1).padStart(2, '0') + '-' + 
         String(today.getDate()).padStart(2, '0');
};

const formatDateLocal = (date) => {
  return date.getFullYear() + '-' +
    String(date.getMonth() + 1).padStart(2, '0') + '-' +
    String(date.getDate()).padStart(2, '0');
};

const getYearMonthKey = (value) => {
  if (!value) return '';

  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})/);
    if (match) return `${match[1]}-${match[2]}`;
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return '';
  return `${parsedDate.getFullYear()}-${String(parsedDate.getMonth() + 1).padStart(2, '0')}`;
};

const processArrayData = (data) => {
  if (data?.pagination?.totalCount !== undefined) {
    return {
      items: data.data || [],
      total: data.pagination.totalCount
    };
  } else if (Array.isArray(data)) {
    return {
      items: data,
      total: data.length
    };
  } else if (data?.data && Array.isArray(data.data)) {
    return {
      items: data.data,
      total: data.data.length
    };
  }
  return { items: [], total: 0 };
};

const HomePageAdmin = () => {
  const navigate = useNavigate();
  const [adminName, setAdminName] = useState("");
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(true); // Veriler yüklenirken
  const [secondaryDataLoading, setSecondaryDataLoading] = useState(true); // Detaylı veriler
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [criticalDataLoaded, setCriticalDataLoaded] = useState(false);
  const [companyReportData, setCompanyReportData] = useState([]);
  const [companyStatsLoading, setCompanyStatsLoading] = useState(false);
  const [allTimeCompanyData, setAllTimeCompanyData] = useState([]);
  const [allTimeCompanyLoading, setAllTimeCompanyLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('current');
  const [stats, setStats] = useState(INITIAL_STATS);
  const [dailyStats, setDailyStats] = useState(INITIAL_DAILY_STATS);
  const [recentActivities, setRecentActivities] = useState([]);
  const [paymentStats, setPaymentStats] = useState(INITIAL_PAYMENT_STATS);
  const [monthlyRevenue, setMonthlyRevenue] = useState(INITIAL_REVENUE);
  const [totalRevenue, setTotalRevenue] = useState(INITIAL_REVENUE);
  const [lessonStats, setLessonStats] = useState(INITIAL_LESSON_STATS);
  const [showTeacherPaymentModal, setShowTeacherPaymentModal] = useState(false);
  const [showPaymentDetailsModal, setShowPaymentDetailsModal] = useState(false);
  const [showCompanyRevenueModal, setShowCompanyRevenueModal] = useState(false);

  const token = useMemo(() => localStorage.getItem("token"), []);
  const adminId = useMemo(() => localStorage.getItem("adminId"), []);
  const adminRole = useMemo(() => localStorage.getItem("adminRole"), []); // Admin rolünü al
  const isSuperAdmin = useMemo(() => adminId === SUPER_ADMIN_ID, [adminId]);

  useEffect(() => {
    if (!token || !adminId) {
      navigate("/admin/login");
      return;
    }

    const fetchAllData = async () => {
      // İlk önce temel verileri çek ve sayfayı göster
      let basicDataFetched = false;
      try {
        // Admin bilgilerini al
        if (!isSuperAdmin) {
          try {
            const adminResponse = await axiosConfig.get(`/admin/all/${adminId}`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });
            setAdminName(adminResponse.data.name || "");
            localStorage.setItem("adminName", adminResponse.data.name || "Admin");
          } catch (adminError) {
            setAdminName("Süper Admin");
            localStorage.setItem("adminName", "Süper Admin");
          }
        } else {
          setAdminName("Süper Admin");
          localStorage.setItem("adminName", "Süper Admin");
        }

        // LOADING'İ HEMEN KALDIR - SAYFA AÇILSIN!
        setLoading(false);
        
        const todayStr = getTodayString();
        const today = new Date();
        const startOfWeek = new Date(today);
        const dayOfWeek = startOfWeek.getDay(); // 0: Pazar, 1: Pazartesi
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        startOfWeek.setDate(startOfWeek.getDate() - daysFromMonday);
        const weekStartStr = formatDateLocal(startOfWeek);
        
        // Tüm verileri paralel olarak çek ama 2 gruba ayır
        // Grup 1: Temel veriler (hızlı)
        const criticalDataPromise = Promise.all([
          axiosConfig.get('/admin/ogrencis?limit=1000', {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axiosConfig.get('/admin/teachers?limit=1000', {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axiosConfig.get('/admin/dersgrubu', {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axiosConfig.get('/admin/sirkets', {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        // Grup 2: Detaylı veriler
        const secondaryDataPromise = Promise.all([
          // Bugünün dersleri - baslangic_tarihi'ne göre filtrele
          axiosConfig.get('/admin/ogretmenders', {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              start_date: todayStr,
              end_date: todayStr,
              date_field: 'baslangic_tarihi',
              limit: 1000
            }
          }).catch(err => ({ data: { data: [], pagination: { totalCount: 0 } } })),
          // Bugünün ödemeleri - created_at kullan
          axiosConfig.get('/admin/odemes', {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              period: 'today',
              limit: 1000
            }
          }).catch(err => ({ data: { data: [], stats: { today_amount: 0 } } })),
          // Bugünün kurs satışları - SADECE COUNT AL
          axiosConfig.get('/admin/kurssatis/daily-count', {
            headers: { Authorization: `Bearer ${token}` }
          }).then(res => ({ data: { total: res.data.count || 0 } }))
            .catch(err => ({ data: { total: 0 } })),
          // Bu haftanın ödemeleri - created_at, pazartesi->bugün (custom aralık)
          axiosConfig.get('/admin/odemes', {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              period: 'custom',
              start_date: weekStartStr,
              end_date: todayStr,
              limit: 1000
            }
          }).catch(err => ({ data: [] })),
          // Bu yılın ödemeleri - created_at kullan
          axiosConfig.get('/admin/odemes', {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              period: 'thisYear',
              year: new Date().getFullYear(),
              limit: 1000
            }
          }).catch(err => ({ data: [] })),
          // Bu ayın dersleri - baslangic_tarihi kullan
          axiosConfig.get('/admin/ogretmenders', {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              start_date: formatDateLocal(new Date(today.getFullYear(), today.getMonth(), 1)),
              end_date: formatDateLocal(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
              date_field: 'baslangic_tarihi',
              limit: 10000
            }
          }).catch(err => ({ data: [] })),
          // Tüm dersler - baslangic_tarihi kullan (toplam ciro hesaplaması)
          axiosConfig.get('/admin/ogretmenders', {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              date_field: 'baslangic_tarihi',
              limit: 100000 // Tüm kayıtları çek
            }
          }).catch(err => ({ data: [] })),
          // Bu haftanın dersleri - baslangic_tarihi kullan
          axiosConfig.get('/admin/ogretmenders', {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              start_date: formatDateLocal(new Date(today.getTime() - (today.getDay() || 7) * 24 * 60 * 60 * 1000 + 24 * 60 * 60 * 1000)),
              end_date: todayStr,
              date_field: 'baslangic_tarihi',
              limit: 10000
            }
          }).catch(err => ({ data: [] })),
          // Bu yılın dersleri - baslangic_tarihi kullan
          axiosConfig.get('/admin/ogretmenders', {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              start_date: formatDateLocal(new Date(today.getFullYear(), 0, 1)),
              end_date: todayStr,
              date_field: 'baslangic_tarihi',
              limit: 100000
            }
          }).catch(err => ({ data: [] }))
        ]);
        
        // Önce kritik verileri bekle ve işle
        const [studentsRes, teachersRes, lessonsRes, companiesRes] = await criticalDataPromise;
        
        // Temel verileri işle
        const studentData = processArrayData(studentsRes.data);
        const teacherData = processArrayData(teachersRes.data);
        const lessonData = processArrayData(lessonsRes.data);
        const companyData = processArrayData(companiesRes.data);
        
        // Temel istatistikleri ayarla ve loading'i kaldır
        setStats(prev => ({
          ...prev,
          studentCount: studentData.total,
          teacherCount: teacherData.total,
          lessonCount: lessonData.total,
          companyCount: companyData.total
        }));
        
        // ARTIK VERİLER GELDİ!
        setDataLoading(false);
        
        // Detaylı verileri bekle
        const [dailyLessonsRes, dailyPaymentsRes, dailyCourseRes, weekPaymentsRes, yearPaymentsRes, monthlyLessonsRes, allLessonsRes, weeklyLessonsRes, yearlyLessonsRes] = await secondaryDataPromise;

        // Detaylı verileri işle
        let students = studentData.items;
        let totalStudentCount = studentData.total;
        let activeStudentCount = 0;
        let passiveStudentCount = 0;

        // Aktif/pasif öğrenci sayısını hesapla
        if (students.length > 0) {
          activeStudentCount = students.filter(s => s.status === 1 || s.status === '1').length;
          passiveStudentCount = students.filter(s => s.status === 0 || s.status === '0').length;
          
          // Eğer tüm öğrenciler yüklenmemişse (pagination varsa), oran üzerinden tahmin et
          if (totalStudentCount > students.length && students.length > 0) {
            const activeRatio = activeStudentCount / students.length;
            const passiveRatio = passiveStudentCount / students.length;
            activeStudentCount = Math.round(totalStudentCount * activeRatio);
            passiveStudentCount = totalStudentCount - activeStudentCount;
          }
        }

        const studentsStartedLessons = students.filter(s => s.registration_status === 1 || s.registration_status === '1');
        const completedStudents = students.filter(s => s.registration_status === 3 || s.registration_status === '3');
        
        // Derse başlayan ve tamamlayan sayıları için de oran hesapla
        let studentsStartedCount = studentsStartedLessons.length;
        let completedCount = completedStudents.length;
        
        if (totalStudentCount > students.length && students.length > 0) {
          const startedRatio = studentsStartedCount / students.length;
          const completedRatio = completedCount / students.length;
          studentsStartedCount = Math.round(totalStudentCount * startedRatio);
          completedCount = Math.round(totalStudentCount * completedRatio);
        }



        // Öğretmen verileri
        let teachers = teacherData.items;
        let totalTeacherCount = teacherData.total;
        let activeTeacherCount = 0;
        let passiveTeacherCount = 0;

        // Aktif/pasif öğretmen sayısını hesapla
        if (teachers.length > 0) {
          activeTeacherCount = teachers.filter(t => t.status === 1 || t.status === '1').length;
          passiveTeacherCount = teachers.filter(t => t.status === 0 || t.status === '0').length;
          
          // Eğer tüm öğretmenler yüklenmemişse (pagination varsa), oran üzerinden tahmin et
          if (totalTeacherCount > teachers.length && teachers.length > 0) {
            const activeRatio = activeTeacherCount / teachers.length;
            const passiveRatio = passiveTeacherCount / teachers.length;
            activeTeacherCount = Math.round(totalTeacherCount * activeRatio);
            passiveTeacherCount = totalTeacherCount - activeTeacherCount;
          }
        }



        // Ders ve Şirket verileri zaten işlendi
        let lessons = lessonData.items;
        let totalLessonCount = lessonData.total;
        let companies = companyData.items;
        let totalCompanyCount = companyData.total;

        // Son aktiviteleri oluştur
        const activities = [];
        
        // Son eklenen öğrenciler
        const recentStudents = students
          .filter(s => s.created_at)
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 2);
        
        recentStudents.forEach(student => {
          activities.push({
            user: student.name || 'Öğrenci',
            action: 'sisteme kaydoldu',
            time: getTimeAgo(student.created_at),
            type: 'enrollment'
          });
        });

        // Son eklenen öğretmenler
        const recentTeachers = teachers
          .filter(t => t.created_at)
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 2);
        
        recentTeachers.forEach(teacher => {
          activities.push({
            user: teacher.adi || 'Öğretmen',
            action: 'öğretmen olarak eklendi',
            time: getTimeAgo(teacher.created_at),
            type: 'teacher'
          });
        });

        // Aktiviteleri zamana göre sırala
        activities.sort((a, b) => {
          const timeOrder = ['az önce', 'dakika önce', 'saat önce', 'gün önce'];
          const aIndex = timeOrder.findIndex(t => a.time.includes(t));
          const bIndex = timeOrder.findIndex(t => b.time.includes(t));
          return aIndex - bIndex;
        });

        setRecentActivities(activities.slice(0, 4));

        // Günlük istatistikleri hesapla
        let dailyLessonCount = 0;
        let dailyLessonRevenue = 0;
        let dailyCompanyRevenue = 0;
        let dailyConsultantPayments = 0;
        let dailyPaymentTotal = 0;
        let dailyCourseCount = 0;

        // Günlük ders sayısı ve ders geliri - API zaten bugünün derslerini döndürüyor
        if (dailyLessonsRes.data) {
          let lessons = [];
          
          if (dailyLessonsRes.data.data && Array.isArray(dailyLessonsRes.data.data)) {
            lessons = dailyLessonsRes.data.data;
          } else if (Array.isArray(dailyLessonsRes.data)) {
            lessons = dailyLessonsRes.data;
          }
          
          // Ders sayısını al
          if (dailyLessonsRes.data.pagination && dailyLessonsRes.data.pagination.totalCount !== undefined) {
            dailyLessonCount = dailyLessonsRes.data.pagination.totalCount;
          } else {
            dailyLessonCount = lessons.length;
          }
          
          // Ders ücretlerini, şirket payını ve danışman ödemelerini topla
          lessons.forEach(lesson => {
            // Öğretmen ödemesi
            dailyLessonRevenue += parseFloat(lesson.ders_ucret) || 0;
            
            // Şirket payı
            dailyCompanyRevenue += parseFloat(lesson.sirket_payi) || 0;
            
            // Danışman payları (danisman1, danisman2, danisman3)
            dailyConsultantPayments += (parseFloat(lesson.danisman1) || 0);
            dailyConsultantPayments += (parseFloat(lesson.danisman2) || 0);
            dailyConsultantPayments += (parseFloat(lesson.danisman3) || 0);
          });
        }

        // Günlük ödeme toplamı - API period=today ile zaten bugünün ödemelerini döndürüyor
        if (dailyPaymentsRes.data) {
          let payments = [];
          
          if (dailyPaymentsRes.data.data && Array.isArray(dailyPaymentsRes.data.data)) {
            payments = dailyPaymentsRes.data.data;
          } else if (Array.isArray(dailyPaymentsRes.data)) {
            payments = dailyPaymentsRes.data;
          }
          
          // Ödemeleri topla
          dailyPaymentTotal = payments.reduce((sum, payment) => {
            return sum + (parseFloat(payment.tutar) || 0);
          }, 0);
          
          // Eğer API response'unda totalAmount varsa onu kullan
          if (dailyPaymentsRes.data.totalAmount !== undefined) {
            dailyPaymentTotal = dailyPaymentsRes.data.totalAmount;
          }
        }

        // Günlük kurs kaydı sayısı - SADECE TOPLAM SAYI
        if (dailyCourseRes.data) {
          dailyCourseCount = dailyCourseRes.data.total || 0;
        }


        
        setDailyStats({
          dailyLessons: dailyLessonCount,
          dailyLessonRevenue: dailyLessonRevenue,
          dailyPayments: dailyPaymentTotal,
          dailyCourseRegistrations: dailyCourseCount,
          dailyCompanyRevenue: dailyCompanyRevenue,
          dailyConsultantPayments: dailyConsultantPayments
        });
        
        // Detaylı veriler de yüklendi
        setSecondaryDataLoading(false);

        // Haftalık ve yıllık ödeme toplamlarını hesapla
        let weekPaymentTotal = 0;
        let yearPaymentTotal = 0;

        // Haftalık ödeme toplamı
        if (weekPaymentsRes.data) {
          let weekPayments = [];
          
          if (weekPaymentsRes.data.data && Array.isArray(weekPaymentsRes.data.data)) {
            weekPayments = weekPaymentsRes.data.data;
          } else if (Array.isArray(weekPaymentsRes.data)) {
            weekPayments = weekPaymentsRes.data;
          }
          
          weekPaymentTotal = weekPayments.reduce((sum, payment) => {
            return sum + (parseFloat(payment.tutar) || 0);
          }, 0);
          
          if (weekPaymentsRes.data.totalAmount !== undefined) {
            weekPaymentTotal = weekPaymentsRes.data.totalAmount;
          }
        }

        // Yıllık ödeme toplamı
        if (yearPaymentsRes.data) {
          let yearPayments = [];
          
          if (yearPaymentsRes.data.data && Array.isArray(yearPaymentsRes.data.data)) {
            yearPayments = yearPaymentsRes.data.data;
          } else if (Array.isArray(yearPaymentsRes.data)) {
            yearPayments = yearPaymentsRes.data;
          }
          
          yearPaymentTotal = yearPayments.reduce((sum, payment) => {
            return sum + (parseFloat(payment.tutar) || 0);
          }, 0);
          
          if (yearPaymentsRes.data.totalAmount !== undefined) {
            yearPaymentTotal = yearPaymentsRes.data.totalAmount;
          }
        }

        setPaymentStats({
          today: dailyPaymentTotal,
          week: weekPaymentTotal,
          year: yearPaymentTotal
        });

        // Aylık ciro hesaplaması
        let teacherPayments = 0;
        let companyRevenue = 0;
        let consultantPayments = 0;

        if (monthlyLessonsRes.data) {
          let monthlyLessons = [];
          
          if (monthlyLessonsRes.data.data && Array.isArray(monthlyLessonsRes.data.data)) {
            monthlyLessons = monthlyLessonsRes.data.data;
          } else if (Array.isArray(monthlyLessonsRes.data)) {
            monthlyLessons = monthlyLessonsRes.data;
          }
          
          // Her ders için ödemeleri hesapla
          monthlyLessons.forEach(lesson => {
            // Öğretmen ödemesi
            teacherPayments += parseFloat(lesson.ders_ucret) || 0;
            
            // Şirket payı
            companyRevenue += parseFloat(lesson.sirket_payi) || 0;
            
            // Danışman payları (danisman1, danisman2, danisman3)
            consultantPayments += (parseFloat(lesson.danisman1) || 0);
            consultantPayments += (parseFloat(lesson.danisman2) || 0);
            consultantPayments += (parseFloat(lesson.danisman3) || 0);
          });
        }

        const totalMonthlyRevenue = teacherPayments + companyRevenue + consultantPayments;

        setMonthlyRevenue({
          teacherPayments,
          companyRevenue,
          consultantPayments,
          totalRevenue: totalMonthlyRevenue
        });

        // Toplam ciro hesaplaması (tüm zamanlar)
        let allTeacherPayments = 0;
        let allCompanyRevenue = 0;
        let allConsultantPayments = 0;

        if (allLessonsRes.data) {
          let allLessons = [];
          
          if (allLessonsRes.data.data && Array.isArray(allLessonsRes.data.data)) {
            allLessons = allLessonsRes.data.data;
          } else if (Array.isArray(allLessonsRes.data)) {
            allLessons = allLessonsRes.data;
          }
          
          // Her ders için ödemeleri hesapla
          allLessons.forEach(lesson => {
            // Öğretmen ödemesi
            allTeacherPayments += parseFloat(lesson.ders_ucret) || 0;
            
            // Şirket payı
            allCompanyRevenue += parseFloat(lesson.sirket_payi) || 0;
            
            // Danışman payları
            allConsultantPayments += (parseFloat(lesson.danisman1) || 0);
            allConsultantPayments += (parseFloat(lesson.danisman2) || 0);
            allConsultantPayments += (parseFloat(lesson.danisman3) || 0);
          });
        }

        const grandTotalRevenue = allTeacherPayments + allCompanyRevenue + allConsultantPayments;

        setTotalRevenue({
          teacherPayments: allTeacherPayments,
          companyRevenue: allCompanyRevenue,
          consultantPayments: allConsultantPayments,
          totalRevenue: grandTotalRevenue
        });

        // Ders istatistiklerini hesapla
        let weeklyLessonCount = 0;
        let yearlyLessonCount = 0;

        // Haftalık ders sayısı
        if (weeklyLessonsRes.data) {
          if (weeklyLessonsRes.data.pagination && weeklyLessonsRes.data.pagination.totalCount !== undefined) {
            weeklyLessonCount = weeklyLessonsRes.data.pagination.totalCount;
          } else if (weeklyLessonsRes.data.data && Array.isArray(weeklyLessonsRes.data.data)) {
            weeklyLessonCount = weeklyLessonsRes.data.data.length;
          } else if (Array.isArray(weeklyLessonsRes.data)) {
            weeklyLessonCount = weeklyLessonsRes.data.length;
          }
        }

        // Yıllık ders sayısı
        if (yearlyLessonsRes.data) {
          if (yearlyLessonsRes.data.pagination && yearlyLessonsRes.data.pagination.totalCount !== undefined) {
            yearlyLessonCount = yearlyLessonsRes.data.pagination.totalCount;
          } else if (yearlyLessonsRes.data.data && Array.isArray(yearlyLessonsRes.data.data)) {
            yearlyLessonCount = yearlyLessonsRes.data.data.length;
          } else if (Array.isArray(yearlyLessonsRes.data)) {
            yearlyLessonCount = yearlyLessonsRes.data.length;
          }
        }

        // Aylık ders sayısı zaten monthlyLessonsRes'ten alınabilir
        let monthlyLessonCount = 0;
        if (monthlyLessonsRes.data) {
          if (monthlyLessonsRes.data.pagination && monthlyLessonsRes.data.pagination.totalCount !== undefined) {
            monthlyLessonCount = monthlyLessonsRes.data.pagination.totalCount;
          } else if (monthlyLessonsRes.data.data && Array.isArray(monthlyLessonsRes.data.data)) {
            monthlyLessonCount = monthlyLessonsRes.data.data.length;
          } else if (Array.isArray(monthlyLessonsRes.data)) {
            monthlyLessonCount = monthlyLessonsRes.data.length;
          }
        }

        setLessonStats({
          today: dailyLessonCount,
          week: weeklyLessonCount,
          month: monthlyLessonCount,
          year: yearlyLessonCount
        });

        // İstatistikleri güncelle - TOPLAM SAYILARI KULLAN
        const finalStats = {
          studentCount: totalStudentCount,
          teacherCount: totalTeacherCount,
          lessonCount: totalLessonCount,
          companyCount: totalCompanyCount,
          activeStudents: activeStudentCount,
          activeTeachers: activeTeacherCount,
          completedLessons: completedCount,
          studentsStartedLessons: studentsStartedCount,
          monthlyRevenue: calculateMonthlyRevenue(students)
        };

        console.log("📊 Final stats:", finalStats);
        console.log("📊 Daily stats:", {
          date: todayStr,
          dailyLessons: dailyLessonCount,
          dailyLessonRevenue: dailyLessonRevenue,
          dailyPayments: dailyPaymentTotal,
          dailyCourseRegistrations: dailyCourseCount,
          dailyCompanyRevenue: dailyCompanyRevenue,
          dailyConsultantPayments: dailyConsultantPayments
        });
        console.log("📊 Payment stats:", {
          today: dailyPaymentTotal,
          week: weekPaymentTotal,
          year: yearPaymentTotal
        });
        console.log("📊 Monthly revenue stats:", {
          teacherPayments,
          companyRevenue,
          consultantPayments,
          totalRevenue: totalMonthlyRevenue
        });
        console.log("📊 Total revenue stats (all time):", {
          teacherPayments: allTeacherPayments,
          companyRevenue: allCompanyRevenue,
          consultantPayments: allConsultantPayments,
          totalRevenue: grandTotalRevenue
        });
        setStats(finalStats);

        // Stats'ı localStorage'a kaydet (Sidebar için) - TOPLAM SAYILARI KULLAN
        localStorage.setItem('dashboardStats', JSON.stringify({
          studentCount: totalStudentCount,
          teacherCount: totalTeacherCount,
          lessonCount: totalLessonCount
        }));

      } catch (error) {        
        if (error.response?.status === 401) {
          localStorage.clear();
          navigate("/admin/login");
        }
        setLoading(false);
      }
    };

    fetchAllData();
  }, [adminId, token, navigate]);

  // Tüm zamanların şirket raporu için veri çekme
  const fetchAllTimeCompanyData = useCallback(async () => {
    if (!token) return;
    
    setAllTimeCompanyLoading(true);
    try {
      const [sirketRes, derslerRes, kursSatisRes, ogrenciRes] = await Promise.all([
        axiosConfig.get('/admin/sirkets', { headers: { Authorization: `Bearer ${token}` }}),
        axiosConfig.get('/admin/ogretmenders', { headers: { Authorization: `Bearer ${token}` }}),
        axiosConfig.get('/admin/kurssatis', { headers: { Authorization: `Bearer ${token}` }}),
        axiosConfig.get('/admin/ogrencis', { headers: { Authorization: `Bearer ${token}` }})
      ]);

      const sirketlerData = sirketRes.data || [];
      const dersler = Array.isArray(derslerRes.data) ? derslerRes.data : (derslerRes.data.data || []);
      const kursSatislar = Array.isArray(kursSatisRes.data) ? kursSatisRes.data : (kursSatisRes.data.data || []);
      const ogrenciler = Array.isArray(ogrenciRes.data) ? ogrenciRes.data : (ogrenciRes.data.data || []);

      const processedData = sirketlerData.map(sirket => {
        // Şirkete kayıtlı öğrenciler
        const sirketOgrencileri = ogrenciler.filter(o => 
          String(o.registered_system) === String(sirket.id)
        );

        // TÜM ZAMANLAR için dersler - filtre yok
        const sirketOgrenciIds = sirketOgrencileri.map(o => o.id);
        let toplamAlacak = 0;
        let dersSayisi = 0;
        
        dersler.forEach(ders => {
          if (sirketOgrenciIds.includes(ders.ogrenci_id)) {
            const sirketPayi = parseFloat(ders.sirket_payi) || 0;
            if (sirketPayi > 0) {
              toplamAlacak += sirketPayi;
              dersSayisi++;
            }
          }
        });

        // Kurs sayısını hesapla
        const kursSayisi = kursSatislar.filter(kurs => 
          sirketOgrenciIds.includes(kurs.musteri_id)
        ).length;

        return {
          id: sirket.id,
          sirket_adi: sirket.sirket_adi,
          status: sirket.status,
          ogrenciSayisi: sirketOgrencileri.length,
          aktifOgrenci: sirketOgrencileri.filter(o => o.status === 1).length,
          kursSayisi: kursSayisi,
          dersSayisi: dersSayisi,
          toplamKazanc: toplamAlacak
        };
      });

      setAllTimeCompanyData(processedData);
    } catch (error) {
      console.error('Tüm zamanlar şirket verileri alınamadı:', error);
    } finally {
      setAllTimeCompanyLoading(false);
    }
  }, [token]);

  // Şirket raporu için veri çekme - useCallback ile optimize edildi
  const fetchCompanyReportData = useCallback(async () => {
    if (!token) return;
    
    setCompanyStatsLoading(true);
    try {
      const [sirketRes, derslerRes, kursSatisRes, ogrenciRes] = await Promise.all([
        axiosConfig.get('/admin/sirkets', { headers: { Authorization: `Bearer ${token}` }}),
        axiosConfig.get('/admin/ogretmenders', { headers: { Authorization: `Bearer ${token}` }}),
        axiosConfig.get('/admin/kurssatis', { headers: { Authorization: `Bearer ${token}` }}),
        axiosConfig.get('/admin/ogrencis', { headers: { Authorization: `Bearer ${token}` }})
      ]);

      const sirketlerData = sirketRes.data || [];
      const dersler = Array.isArray(derslerRes.data) ? derslerRes.data : (derslerRes.data.data || []);
      const kursSatislar = Array.isArray(kursSatisRes.data) ? kursSatisRes.data : (kursSatisRes.data.data || []);
      const ogrenciler = Array.isArray(ogrenciRes.data) ? ogrenciRes.data : (ogrenciRes.data.data || []);

      // Dönem bazlı filtreleme
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1;
      const currentYear = currentDate.getFullYear();
      const currentMonthKey = `${currentYear}-${String(currentMonth).padStart(2, '0')}`;

      const processedData = sirketlerData.map(sirket => {
        // Şirkete kayıtlı öğrenciler
        const sirketOgrencileri = ogrenciler.filter(o => 
          String(o.registered_system) === String(sirket.id)
        );

        // Bu ayın derslerini filtrele
        const monthlyDersler = dersler.filter((ders) =>
          getYearMonthKey(ders.baslangic_tarihi) === currentMonthKey
        );

        // Şirket öğrencilerinin derslerini hesapla
        const sirketOgrenciIds = sirketOgrencileri.map(o => o.id);
        let toplamAlacak = 0;
        let dersSayisi = 0;
        
        monthlyDersler.forEach(ders => {
          if (sirketOgrenciIds.includes(ders.ogrenci_id)) {
            const sirketPayi = parseFloat(ders.sirket_payi) || 0;
            if (sirketPayi > 0) {
              toplamAlacak += sirketPayi;
              dersSayisi++;
            }
          }
        });

        // Kurs sayısını hesapla
        const kursSayisi = kursSatislar.filter(kurs => 
          sirketOgrenciIds.includes(kurs.musteri_id)
        ).length;

        return {
          id: sirket.id,
          sirket_adi: sirket.sirket_adi,
          status: sirket.status,
          ogrenciSayisi: sirketOgrencileri.length,
          aktifOgrenci: sirketOgrencileri.filter(o => o.status === 1).length,
          kursSayisi: kursSayisi,
          dersSayisi: dersSayisi,
          aylikAlacak: toplamAlacak
        };
      });

      setCompanyReportData(processedData);
    } catch (error) {
      // Error handling
    } finally {
      setCompanyStatsLoading(false);
    }
  }, [token]);

  // Şirket rapor verilerini çek
  useEffect(() => {
    fetchCompanyReportData();
    fetchAllTimeCompanyData();
  }, [fetchCompanyReportData, fetchAllTimeCompanyData]);

  // Bu fonksiyonlar artık component dışında tanımlı

  // StatCard ve ActivityItem artık component dışında memoized olarak tanımlı

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">
          Hoş geldiniz, {adminName || "Admin"}! 👋
        </h1>
        <p className="text-gray-600 mt-2">İşte bugünkü özet bilgileriniz</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {dataLoading ? (
          // Yükleniyor animasyonu
          [...Array(4)].map((_, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm p-6">
              <div className="animate-pulse">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
                </div>
                <div className="h-8 bg-gray-200 rounded w-24 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                <div className="h-3 bg-gray-100 rounded w-full"></div>
              </div>
            </div>
          ))
        ) : (
          <>
            <StatCard
              icon={FiUsers}
              label="Toplam Öğrenci"
              value={stats.studentCount}
              color="from-blue-500 to-blue-600"
              trend={12}
              details={[
                { label: 'Aktif', value: stats.activeStudents, color: 'text-green-600' },
                { label: 'Pasif', value: stats.studentCount - stats.activeStudents, color: 'text-red-600' },
                { label: 'Derse Başlayan', value: stats.studentsStartedLessons, color: 'text-blue-600' },
                { label: 'Dersi Tamamlayan', value: stats.completedLessons, color: 'text-purple-600' }
              ]}
            />
            <StatCard
              icon={FiUserCheck}
              label="Toplam Öğretmen"
              value={stats.teacherCount}
              color="from-green-500 to-green-600"
              trend={8}
              details={[
                { label: 'Aktif', value: stats.activeTeachers, color: 'text-green-600' },
                { label: 'Pasif', value: stats.teacherCount - stats.activeTeachers, color: 'text-red-600' }
              ]}
            />
            <StatCard
              icon={FiBook}
              label="Ders Grupları"
              value={stats.lessonCount}
              subValue="Aktif gruplar"
              color="from-purple-500 to-purple-600"
            />
            <StatCard
              icon={FiBriefcase}
              label="Şirketler"
              value={stats.companyCount}
              subValue="Kayıtlı şirket"
              color="from-orange-500 to-orange-600"
            />
          </>
        )}
      </div>

      {/* Günlük Rapor */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl shadow-lg p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white flex items-center">
            <FiCalendar className="mr-2" />
            Günlük Rapor - {new Date().toLocaleDateString('tr-TR', DATE_FORMAT_OPTIONS)}
          </h2>
        </div>
        {secondaryDataLoading ? (
          // Yükleniyor animasyonu
          <div className="flex items-center justify-center h-32">
            <div className="text-white">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white mb-2 mx-auto"></div>
              <p className="text-sm">Veriler yükleniyor...</p>
            </div>
          </div>
        ) : (
        <div className={`grid grid-cols-1 md:grid-cols-2 ${isSuperAdmin ? 'lg:grid-cols-3 xl:grid-cols-5' : 'lg:grid-cols-2'} gap-4`}>
          <div
            className={`bg-white/20 backdrop-blur rounded-lg p-4 ${isSuperAdmin ? 'cursor-pointer hover:bg-white/30' : ''} transition-all duration-200 ${isSuperAdmin ? 'transform hover:scale-105' : ''}`}
            onClick={isSuperAdmin ? () => setShowTeacherPaymentModal(true) : undefined}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-white/30 rounded-lg">
                <FiUserCheck className="text-white text-xl" />
              </div>
              {isSuperAdmin && (
                <span className="text-2xl font-bold text-white">₺{dailyStats.dailyLessonRevenue.toLocaleString('tr-TR')}</span>
              )}
            </div>
            <p className="text-sm text-white/90">Öğretmen Ders Ücreti</p>
            <p className="text-xs text-white/70 mt-1">Bugün {dailyStats.dailyLessons} ders verildi</p>
            {isSuperAdmin && (
              <p className="text-xs text-white/50 mt-1 flex items-center">
                <FiEye className="mr-1" />
                Detayları görüntülemek için tıklayın
              </p>
            )}
          </div>

          {isSuperAdmin && (
            <div 
              className="bg-white/20 backdrop-blur rounded-lg p-4 cursor-pointer hover:bg-white/30 transition-all duration-200 transform hover:scale-105"
              onClick={() => setShowPaymentDetailsModal(true)}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-white/30 rounded-lg">
                  <FiCreditCard className="text-white text-xl" />
                </div>
                <span className="text-2xl font-bold text-white">₺{dailyStats.dailyPayments.toLocaleString('tr-TR')}</span>
              </div>
              <p className="text-sm text-white/90">Alınan Ödeme</p>
              <p className="text-xs text-white/70 mt-1">Bugün tahsil edilen</p>
              <p className="text-xs text-white/50 mt-1 flex items-center">
                <FiEye className="mr-1" />
                Detayları görüntülemek için tıklayın
              </p>
            </div>
          )}
          
          <div className="bg-white/20 backdrop-blur rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-white/30 rounded-lg">
                <FiFileText className="text-white text-xl" />
              </div>
              <span className="text-2xl font-bold text-white">{dailyStats.dailyCourseRegistrations}</span>
            </div>
            <p className="text-sm text-white/90">Kurs Kaydı</p>
            <p className="text-xs text-white/70 mt-1">Yeni kayıtlar</p>
          </div>
          
          {isSuperAdmin && (
            <>
              <div 
                className="bg-white/20 backdrop-blur rounded-lg p-4 cursor-pointer hover:bg-white/30 transition-all duration-200 transform hover:scale-105"
                onClick={() => setShowCompanyRevenueModal(true)}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="p-2 bg-white/30 rounded-lg">
                    <FiBriefcase className="text-white text-xl" />
                  </div>
                  <span className="text-2xl font-bold text-white">₺{dailyStats.dailyCompanyRevenue.toLocaleString('tr-TR')}</span>
                </div>
                <p className="text-sm text-white/90">Şirket Kazancı</p>
                <p className="text-xs text-white/70 mt-1">Günlük net kazanç</p>
                <p className="text-xs text-white/50 mt-1 flex items-center">
                  <FiEye className="mr-1" />
                  Detayları görüntülemek için tıklayın
                </p>
              </div>
              
              <div className="bg-white/20 backdrop-blur rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="p-2 bg-white/30 rounded-lg">
                    <FiUsers className="text-white text-xl" />
                  </div>
                  <span className="text-2xl font-bold text-white">₺{dailyStats.dailyConsultantPayments.toLocaleString('tr-TR')}</span>
                </div>
                <p className="text-sm text-white/90">Danışman Ödemeleri</p>
                <p className="text-xs text-white/70 mt-1">Günlük toplam</p>
              </div>
              
              <div className="bg-white/20 backdrop-blur rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="p-2 bg-white/30 rounded-lg">
                    <FiDollarSign className="text-white text-xl" />
                  </div>
                  <span className="text-2xl font-bold text-white">
                    ₺{(dailyStats.dailyLessonRevenue + dailyStats.dailyCompanyRevenue + dailyStats.dailyConsultantPayments).toLocaleString('tr-TR')}
                  </span>
                </div>
                <p className="text-sm text-white/90">Toplam Ciro</p>
                <p className="text-xs text-white/70 mt-1">Öğretmen + Danışman + Şirket</p>
              </div>
            </>
          )}
        </div>
        )}
      </div>

      {/* Şirket Rapor Tablosu */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800">
            Bu Ay - Tüm Şirketler
          </h3>
        </div>
        
        {companyStatsLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-500"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Şirket Adı</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Öğrenci</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Aktif</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Pasif</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Kurs</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Ders</th>
                  {adminId === 'superadmin-001' && (
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Bu Ay Alacağı</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {companyReportData.map((sirket, index) => (
                  <tr key={sirket.id} className={`transition-colors hover:bg-gray-50 ${
                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                  }`}>
                    <td className="py-3 px-4">
                      <p className="font-medium text-gray-800">{sirket.sirket_adi}</p>
                    </td>
                    <td className="text-center py-3 px-4">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full font-semibold text-sm">
                        {sirket.ogrenciSayisi || 0}
                      </span>
                    </td>
                    <td className="text-center py-3 px-4">
                      <span className="text-sm font-medium text-green-600">
                        {sirket.aktifOgrenci || 0}
                      </span>
                    </td>
                    <td className="text-center py-3 px-4">
                      <span className="text-sm font-medium text-red-600">
                        {(sirket.ogrenciSayisi - sirket.aktifOgrenci) || 0}
                      </span>
                    </td>
                    <td className="text-center py-3 px-4">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-purple-100 text-purple-700 rounded-full font-semibold text-sm">
                        {sirket.kursSayisi || 0}
                      </span>
                    </td>
                    <td className="text-center py-3 px-4">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-700 rounded-full font-semibold text-sm">
                        {sirket.dersSayisi || 0}
                      </span>
                    </td>
                    {isSuperAdmin && (
                      <td className="text-right py-3 px-4">
                        <span className={`font-bold ${
                          sirket.aylikAlacak > 0 ? 'text-green-600' : 'text-gray-400'
                        }`}>
                          {formatCurrency(sirket.aylikAlacak)}
                        </span>
                      </td>
                    )}
                  </tr>
                ))}
                
                {/* Genel Toplam Satırı */}
                {companyReportData.length > 0 && (
                  <tr className="bg-gray-100 font-bold">
                    <td className="py-3 px-4">
                      <span className="text-sm font-bold text-gray-900">
                        GENEL TOPLAM
                      </span>
                    </td>
                    <td className="text-center py-3 px-4">
                      <span className="text-sm text-gray-900">
                        {companyReportData.reduce((sum, c) => sum + c.ogrenciSayisi, 0)}
                      </span>
                    </td>
                    <td className="text-center py-3 px-4">
                      <span className="text-sm text-green-600">
                        {companyReportData.reduce((sum, c) => sum + c.aktifOgrenci, 0)}
                      </span>
                    </td>
                    <td className="text-center py-3 px-4">
                      <span className="text-sm text-red-600">
                        {companyReportData.reduce((sum, c) => sum + (c.ogrenciSayisi - c.aktifOgrenci), 0)}
                      </span>
                    </td>
                    <td className="text-center py-3 px-4">
                      <span className="text-sm text-gray-900">
                        {companyReportData.reduce((sum, c) => sum + c.kursSayisi, 0)}
                      </span>
                    </td>
                    <td className="text-center py-3 px-4">
                      <span className="text-sm text-gray-900">
                        {companyReportData.reduce((sum, c) => sum + c.dersSayisi, 0)}
                      </span>
                    </td>
                    {isSuperAdmin && (
                      <td className="text-right py-3 px-4">
                        <span className="text-sm text-green-600">
                          {formatCurrency(companyReportData.reduce((sum, c) => sum + c.aylikAlacak, 0))}
                        </span>
                      </td>
                    )}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Yapılan Dersler Raporu */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl shadow-lg p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white flex items-center">
            <FiBook className="mr-2" />
            Yapılan Dersler Raporu
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white/20 backdrop-blur rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-white/30 rounded-lg">
                <FiCalendar className="text-white text-xl" />
              </div>
              <span className="text-2xl font-bold text-white">{lessonStats.today}</span>
            </div>
            <p className="text-sm text-white/90">Bugün</p>
            <p className="text-xs text-white/70 mt-1">Günlük yapılan ders</p>
          </div>

          <div className="bg-white/20 backdrop-blur rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-white/30 rounded-lg">
                <FiTrendingUp className="text-white text-xl" />
              </div>
              <span className="text-2xl font-bold text-white">{lessonStats.week}</span>
            </div>
            <p className="text-sm text-white/90">Bu Hafta</p>
            <p className="text-xs text-white/70 mt-1">Haftalık yapılan ders</p>
          </div>

          <div className="bg-white/20 backdrop-blur rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-white/30 rounded-lg">
                <FiActivity className="text-white text-xl" />
              </div>
              <span className="text-2xl font-bold text-white">{lessonStats.month}</span>
            </div>
            <p className="text-sm text-white/90">Bu Ay</p>
            <p className="text-xs text-white/70 mt-1">Aylık yapılan ders</p>
          </div>

          <div className="bg-white/20 backdrop-blur rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-white/30 rounded-lg">
                <FiDollarSign className="text-white text-xl" />
              </div>
              <span className="text-2xl font-bold text-white">{lessonStats.year}</span>
            </div>
            <p className="text-sm text-white/90">Bu Yıl</p>
            <p className="text-xs text-white/70 mt-1">{new Date().getFullYear()} toplam ders</p>
          </div>
        </div>
      </div>

      {/* Gelen Ödemeler Raporu - Sadece Süper Admin */}
      {isSuperAdmin && (
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white flex items-center">
              <FiCreditCard className="mr-2" />
              Gelen Ödemeler Raporu
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white/20 backdrop-blur rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-white/30 rounded-lg">
                  <FiCalendar className="text-white text-xl" />
                </div>
                <span className="text-2xl font-bold text-white">₺{paymentStats.today.toLocaleString('tr-TR')}</span>
              </div>
              <p className="text-sm text-white/90">Bugün</p>
              <p className="text-xs text-white/70 mt-1">Günlük toplam ödeme</p>
            </div>

            <div className="bg-white/20 backdrop-blur rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-white/30 rounded-lg">
                  <FiTrendingUp className="text-white text-xl" />
                </div>
                <span className="text-2xl font-bold text-white">₺{paymentStats.week.toLocaleString('tr-TR')}</span>
              </div>
              <p className="text-sm text-white/90">Bu Hafta</p>
              <p className="text-xs text-white/70 mt-1">Haftalık toplam ödeme</p>
            </div>

            <div className="bg-white/20 backdrop-blur rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-white/30 rounded-lg">
                  <FiDollarSign className="text-white text-xl" />
                </div>
                <span className="text-2xl font-bold text-white">₺{paymentStats.year.toLocaleString('tr-TR')}</span>
              </div>
              <p className="text-sm text-white/90">Bu Yıl</p>
              <p className="text-xs text-white/70 mt-1">{new Date().getFullYear()} toplam ödeme</p>
            </div>
          </div>
        </div>
      )}

      {/* Aylık Ciro Raporu - Sadece Süper Admin */}
      {isSuperAdmin && (
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white flex items-center">
              <FiTrendingUp className="mr-2" />
              Aylık Ciro Raporu - {new Date().toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white/20 backdrop-blur rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-white/30 rounded-lg">
                  <FiUserCheck className="text-white text-xl" />
                </div>
                <span className="text-2xl font-bold text-white">₺{monthlyRevenue.teacherPayments.toLocaleString('tr-TR')}</span>
              </div>
              <p className="text-sm text-white/90">Öğretmen Ödemeleri</p>
              <p className="text-xs text-white/70 mt-1">Ödenecek toplam</p>
            </div>
            
            <div className="bg-white/20 backdrop-blur rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-white/30 rounded-lg">
                  <FiBriefcase className="text-white text-xl" />
                </div>
                <span className="text-2xl font-bold text-white">₺{monthlyRevenue.companyRevenue.toLocaleString('tr-TR')}</span>
              </div>
              <p className="text-sm text-white/90">Şirket Kazancı</p>
              <p className="text-xs text-white/70 mt-1">Net kazanç</p>
            </div>
            
            <div className="bg-white/20 backdrop-blur rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-white/30 rounded-lg">
                  <FiUsers className="text-white text-xl" />
                </div>
                <span className="text-2xl font-bold text-white">₺{monthlyRevenue.consultantPayments.toLocaleString('tr-TR')}</span>
              </div>
              <p className="text-sm text-white/90">Danışman Ödemeleri</p>
              <p className="text-xs text-white/70 mt-1">Verilecek toplam</p>
            </div>
            
            <div className="bg-white/20 backdrop-blur rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-white/30 rounded-lg">
                  <FiDollarSign className="text-white text-xl" />
                </div>
                <span className="text-2xl font-bold text-white">₺{monthlyRevenue.totalRevenue.toLocaleString('tr-TR')}</span>
              </div>
              <p className="text-sm text-white/90">Toplam Ciro</p>
              <p className="text-xs text-white/70 mt-1">Aylık toplam</p>
            </div>
          </div>
        </div>
      )}

      {/* Toplam Ciro Raporu - Sadece Süper Admin */}
      {isSuperAdmin && (
        <div className="bg-gradient-to-r from-amber-600 to-orange-600 rounded-xl shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white flex items-center">
              <FiDollarSign className="mr-2" />
              Toplam Ciro Raporu - Tüm Zamanlar
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white/20 backdrop-blur rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-white/30 rounded-lg">
                  <FiUserCheck className="text-white text-xl" />
                </div>
                <span className="text-2xl font-bold text-white">₺{totalRevenue.teacherPayments.toLocaleString('tr-TR')}</span>
              </div>
              <p className="text-sm text-white/90">Toplam Öğretmen Ödemeleri</p>
              <p className="text-xs text-white/70 mt-1">Tüm zamanlar</p>
            </div>
            
            <div className="bg-white/20 backdrop-blur rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-white/30 rounded-lg">
                  <FiBriefcase className="text-white text-xl" />
                </div>
                <span className="text-2xl font-bold text-white">₺{totalRevenue.companyRevenue.toLocaleString('tr-TR')}</span>
              </div>
              <p className="text-sm text-white/90">Toplam Şirket Kazancı</p>
              <p className="text-xs text-white/70 mt-1">Tüm zamanlar</p>
            </div>
            
            <div className="bg-white/20 backdrop-blur rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-white/30 rounded-lg">
                  <FiUsers className="text-white text-xl" />
                </div>
                <span className="text-2xl font-bold text-white">₺{totalRevenue.consultantPayments.toLocaleString('tr-TR')}</span>
              </div>
              <p className="text-sm text-white/90">Toplam Danışman Ödemeleri</p>
              <p className="text-xs text-white/70 mt-1">Tüm zamanlar</p>
            </div>
            
            <div className="bg-white/20 backdrop-blur rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-white/30 rounded-lg">
                  <FiActivity className="text-white text-xl" />
                </div>
                <span className="text-2xl font-bold text-white">₺{totalRevenue.totalRevenue.toLocaleString('tr-TR')}</span>
              </div>
              <p className="text-sm text-white/90">Genel Toplam Ciro</p>
              <p className="text-xs text-white/70 mt-1">Tüm zamanlar</p>
            </div>
          </div>
        </div>
      )}

      {/* Second Row Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Öğrenci Durumu</h3>
            <FiActivity className="text-gray-400" />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Derse Başladı</span>
              <span className="font-semibold text-gray-800">{stats.studentsStartedLessons}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Tamamladı</span>
              <span className="font-semibold text-gray-800">{stats.completedLessons}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Beklemede</span>
              <span className="font-semibold text-gray-800">
                {stats.studentCount - stats.studentsStartedLessons}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Son Aktiviteler</h3>
            <FiClock className="text-gray-400" />
          </div>
          <div className="space-y-1">
            {recentActivities.length > 0 ? (
              recentActivities.map((activity, index) => (
                <ActivityItem key={index} {...activity} />
              ))
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                Henüz aktivite bulunmuyor
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Tüm Zamanlar Şirket Rapor Tablosu */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800">
            Tüm Zamanlar - Toplam Kazançlar
          </h3>
        </div>
        
        {allTimeCompanyLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-500"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Şirket Adı</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Öğrenci</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Aktif</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Pasif</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Kurs</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Ders</th>
                  {adminId === 'superadmin-001' && (
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Toplam Kazanç</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {allTimeCompanyData.map((sirket, index) => (
                  <tr key={sirket.id} className={`transition-colors hover:bg-gray-50 ${
                    index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                  }`}>
                    <td className="py-3 px-4">
                      <p className="font-medium text-gray-800">{sirket.sirket_adi}</p>
                    </td>
                    <td className="text-center py-3 px-4">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full font-semibold text-sm">
                        {sirket.ogrenciSayisi || 0}
                      </span>
                    </td>
                    <td className="text-center py-3 px-4">
                      <span className="text-sm font-medium text-green-600">
                        {sirket.aktifOgrenci || 0}
                      </span>
                    </td>
                    <td className="text-center py-3 px-4">
                      <span className="text-sm font-medium text-red-600">
                        {(sirket.ogrenciSayisi - sirket.aktifOgrenci) || 0}
                      </span>
                    </td>
                    <td className="text-center py-3 px-4">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-purple-100 text-purple-700 rounded-full font-semibold text-sm">
                        {sirket.kursSayisi || 0}
                      </span>
                    </td>
                    <td className="text-center py-3 px-4">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-700 rounded-full font-semibold text-sm">
                        {sirket.dersSayisi || 0}
                      </span>
                    </td>
                    {isSuperAdmin && (
                      <td className="text-right py-3 px-4">
                        <span className={`font-bold ${
                          sirket.toplamKazanc > 0 ? 'text-green-600' : 'text-gray-400'
                        }`}>
                          {formatCurrency(sirket.toplamKazanc)}
                        </span>
                      </td>
                    )}
                  </tr>
                ))}
                
                {/* Genel Toplam Satırı */}
                {allTimeCompanyData.length > 0 && (
                  <tr className="bg-gradient-to-r from-purple-50 to-indigo-50 font-bold">
                    <td className="py-4 px-4">
                      <span className="text-lg font-bold text-gray-900">
                        GENEL TOPLAM
                      </span>
                    </td>
                    <td className="text-center py-4 px-4">
                      <span className="text-lg text-gray-900">
                        {allTimeCompanyData.reduce((sum, c) => sum + c.ogrenciSayisi, 0)}
                      </span>
                    </td>
                    <td className="text-center py-4 px-4">
                      <span className="text-lg text-green-600">
                        {allTimeCompanyData.reduce((sum, c) => sum + c.aktifOgrenci, 0)}
                      </span>
                    </td>
                    <td className="text-center py-4 px-4">
                      <span className="text-lg text-red-600">
                        {allTimeCompanyData.reduce((sum, c) => sum + (c.ogrenciSayisi - c.aktifOgrenci), 0)}
                      </span>
                    </td>
                    <td className="text-center py-4 px-4">
                      <span className="text-lg text-gray-900">
                        {allTimeCompanyData.reduce((sum, c) => sum + c.kursSayisi, 0)}
                      </span>
                    </td>
                    <td className="text-center py-4 px-4">
                      <span className="text-lg text-gray-900">
                        {allTimeCompanyData.reduce((sum, c) => sum + c.dersSayisi, 0)}
                      </span>
                    </td>
                    {isSuperAdmin && (
                      <td className="text-right py-4 px-4">
                        <span className="text-xl text-green-600">
                          {formatCurrency(allTimeCompanyData.reduce((sum, c) => sum + c.toplamKazanc, 0))}
                        </span>
                      </td>
                    )}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Hızlı İşlemler</h3>
        <div className={`grid grid-cols-2 ${isSuperAdmin ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4`}>
          <button
            onClick={() => navigate('/admin/student-management/all')}
            className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <FiUsers className="text-2xl text-blue-600 mb-2" />
            <span className="text-sm text-gray-700">Öğrenciler</span>
          </button>
          <button
            onClick={() => navigate('/admin/teacher-management/all')}
            className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <FiUserCheck className="text-2xl text-green-600 mb-2" />
            <span className="text-sm text-gray-700">Öğretmenler</span>
          </button>
          <button
            onClick={() => navigate('/admin/lesson-groups')}
            className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <FiBook className="text-2xl text-purple-600 mb-2" />
            <span className="text-sm text-gray-700">Ders Grupları</span>
          </button>
          {adminId === 'superadmin-001' && (
            <button
              onClick={() => navigate('/admin/accounting/gelen-odemeler')}
              className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <FiDollarSign className="text-2xl text-orange-600 mb-2" />
              <span className="text-sm text-gray-700">Ödemeler</span>
            </button>
          )}
        </div>
      </div>

      {/* Teacher Payment Details Modal */}
      <TeacherPaymentDetailsModal 
        isOpen={showTeacherPaymentModal}
        onClose={() => setShowTeacherPaymentModal(false)}
      />
      
      {/* Payment Details Modal */}
      <PaymentDetailsModal 
        isOpen={showPaymentDetailsModal}
        onClose={() => setShowPaymentDetailsModal(false)}
      />
      
      {/* Company Revenue Details Modal */}
      <CompanyRevenueDetailsModal 
        isOpen={showCompanyRevenueModal}
        onClose={() => setShowCompanyRevenueModal(false)}
      />
    </div>
  );
};

export default HomePageAdmin;
